import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, X, Pencil, ChevronUp, ChevronDown, CopyPlus } from "lucide-react";

const EMPTY = { nome: "", descrizione: "", gruppo: "", importo_default: "", steps: [], campi_obbligatori: [], campi_pratica: [] , documenti_richiesti: [] };

const DOC_PRESET = [
  { nome: "Passaporto", obbligatorio: true, max_mb: 2 },
  { nome: "Codice fiscale", obbligatorio: true, max_mb: 1 },
  { nome: "Documento d'identità", obbligatorio: true, max_mb: 2 },
  { nome: "Certificato di residenza", obbligatorio: false, max_mb: 2 },
  { nome: "Marca da bollo 16 euro", obbligatorio: false, max_mb: 1 },
];
const uid = () => Math.random().toString(36).slice(2) + Date.now().toString(36);

const SPONSOR_PRESET = [
  "Nome e cognome sponsor", "Codice fiscale sponsor", "Data di nascita sponsor", "Luogo di nascita sponsor",
  "Indirizzo sponsor", "Email sponsor", "Telefono sponsor", "Rapporto con il richiedente",
].map((label) => ({ key: "", label, gruppo: "Sponsor", obbligatorio: false }));

export default function TipiPratica() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["tipi"], queryFn: async () => (await api.get("/tipi-pratica")).data });
  const { data: gruppi } = useQuery({ queryKey: ["tipi-gruppi"], queryFn: async () => (await api.get("/tipi-pratica/gruppi")).data });
  const { data: fieldsData } = useQuery({ queryKey: ["anagrafica-fields"], queryFn: async () => (await api.get("/anagrafica-fields")).data });
  const anagFields = fieldsData?.fields || [];
  const anagGroups = fieldsData?.groups || [];
  const [editingId, setEditingId] = useState(null);
  // I campi personalizzati compaiono solo nei tipi di pratica a cui sono assegnati
  // (nessuna assegnazione = validi per tutti). I predefiniti valgono sempre.
  const visibleForTipo = (f) => f.builtin || !(f.tipi_pratica || []).length
    || (editingId && (f.tipi_pratica || []).includes(editingId));
  const groupedReq = anagGroups
    .map((g) => ({ key: g.key, label: g.label, fields: anagFields.filter((f) => (f.group || "altri") === g.key && visibleForTipo(f)) }))
    .filter((g) => g.fields.length);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [stepName, setStepName] = useState("");
  const [customLabel, setCustomLabel] = useState("");
  const [cpLabel, setCpLabel] = useState("");
  const [docNome, setDocNome] = useState("");
  const [docMax, setDocMax] = useState("");
  const [cpGruppo, setCpGruppo] = useState("");
  const tipi = data || [];

  const openNew = () => { setEditingId(null); setForm(EMPTY); setStepName(""); setOpen(true); };
  const duplica = (t) => {
    setEditingId(null);  // sarà creato come nuovo tipo
    setForm({
      nome: `${t.nome} (copia)`, codice: "", descrizione: t.descrizione || "", gruppo: t.gruppo || "",
      importo_default: String(t.importo_default ?? ""),
      steps: (t.steps || []).map((s) => ({ ...s, _k: uid(), completato: false })),
      campi_obbligatori: [...(t.campi_obbligatori || [])],
      campi_pratica: (t.campi_pratica || []).map((c) => ({ ...c, key: "" })),
      documenti_richiesti: (t.documenti_richiesti || []).map((d) => ({ ...d, key: "" })),
    });
    setStepName(""); setOpen(true);
    toast.info("Copia pronta: modifica il nome e salva per creare il nuovo tipo di pratica.");
  };

  const openEdit = (t) => {
    setEditingId(t.id);
    setForm({ nome: t.nome, codice: t.codice || "", descrizione: t.descrizione || "", gruppo: t.gruppo || "", importo_default: String(t.importo_default ?? ""), steps: (t.steps || []).map((s) => ({ ...s, _k: uid() })), campi_obbligatori: t.campi_obbligatori || [], campi_pratica: (t.campi_pratica || []).map((c) => ({ ...c })), documenti_richiesti: (t.documenti_richiesti || []).map((d) => ({ ...d })) });
    setStepName(""); setOpen(true);
  };
  const toggleReq = (key) => setForm((f) => ({ ...f, campi_obbligatori: f.campi_obbligatori.includes(key) ? f.campi_obbligatori.filter((k) => k !== key) : [...f.campi_obbligatori, key] }));

  const addStep = () => {
    if (!stepName.trim()) return;
    setForm({ ...form, steps: [...form.steps, { nome: stepName, descrizione: "", richiede_documento: false, richiede_scadenza: false }] });
    setStepName("");
  };
  const setStep = (i, nome) => setForm({ ...form, steps: form.steps.map((s, j) => j === i ? { ...s, nome } : s) });
  const moveStep = (i, dir) => {
    const j = i + dir; if (j < 0 || j >= form.steps.length) return;
    const copy = [...form.steps]; [copy[i], copy[j]] = [copy[j], copy[i]]; setForm({ ...form, steps: copy });
  };
  const removeStep = (i) => setForm({ ...form, steps: form.steps.filter((_, j) => j !== i) });

  const addCustomField = async () => {
    const label = customLabel.trim();
    if (!label) return;
    if (!editingId) return toast.error("Salva prima il tipo di pratica, poi aggiungi i campi personalizzati");
    try {
      const { data: res } = await api.post("/anagrafica-fields", { label, tipi_pratica: [editingId] });
      const newField = [...(res.fields || [])].reverse().find((f) => f.label === label && !f.builtin);
      await qc.invalidateQueries({ queryKey: ["anagrafica-fields"] });
      if (newField) setForm((f) => ({ ...f, campi_obbligatori: f.campi_obbligatori.includes(newField.key) ? f.campi_obbligatori : [...f.campi_obbligatori, newField.key] }));
      setCustomLabel("");
      toast.success(`Campo "${label}" aggiunto a questo tipo di pratica e reso obbligatorio`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore nell'aggiunta del campo"); }
  };

  const closeDialog = () => { setOpen(false); setEditingId(null); setForm(EMPTY); };
  useEffect(() => {
    if (!open) return;
    const onEsc = (e) => e.key === "Escape" && closeDialog();
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [open]);

  const addDoc = () => {
    const nome = docNome.trim();
    if (!nome) return;
    setForm((f) => ({ ...f, documenti_richiesti: [...(f.documenti_richiesti || []),
      { key: "", nome, obbligatorio: true, max_mb: docMax ? Number(docMax) : null }] }));
    setDocNome(""); setDocMax("");
  };
  const addDocPreset = () => setForm((f) => ({
    ...f,
    documenti_richiesti: [...(f.documenti_richiesti || []),
      ...DOC_PRESET.filter((d) => !(f.documenti_richiesti || []).some((x) => x.nome === d.nome)).map((d) => ({ ...d, key: "" }))],
  }));
  const removeDoc = (i) => setForm((f) => ({ ...f, documenti_richiesti: (f.documenti_richiesti || []).filter((_, j) => j !== i) }));
  const patchDoc = (i, patch) => setForm((f) => ({ ...f, documenti_richiesti: (f.documenti_richiesti || []).map((d, j) => j === i ? { ...d, ...patch } : d) }));

  const addCampoPratica = () => {
    const label = cpLabel.trim();
    if (!label) return;
    setForm((f) => ({ ...f, campi_pratica: [...(f.campi_pratica || []), { key: "", label, gruppo: cpGruppo.trim(), obbligatorio: false }] }));
    setCpLabel("");
  };
  const removeCampoPratica = (i) => setForm((f) => ({ ...f, campi_pratica: (f.campi_pratica || []).filter((_, j) => j !== i) }));
  const toggleCampoObbl = (i) => setForm((f) => ({ ...f, campi_pratica: (f.campi_pratica || []).map((c, j) => j === i ? { ...c, obbligatorio: !c.obbligatorio } : c) }));
  const addSponsorPreset = () => setForm((f) => ({
    ...f,
    campi_pratica: [...(f.campi_pratica || []), ...SPONSOR_PRESET.filter((p) => !(f.campi_pratica || []).some((c) => c.label === p.label)).map((p) => ({ ...p }))],
  }));

  const save = async () => {
    if (!form.nome) return toast.error("Nome obbligatorio");
    const payload = { ...form, importo_default: Number(form.importo_default) || 0,
      steps: (form.steps || []).map(({ _k, ...s }) => s) };
    try {
      if (editingId) { await api.put(`/tipi-pratica/${editingId}`, payload); toast.success("Tipo pratica aggiornato"); }
      else { await api.post("/tipi-pratica", payload); toast.success("Tipo pratica creato"); }
      setOpen(false); setForm(EMPTY); setEditingId(null); qc.invalidateQueries({ queryKey: ["tipi"] });
    } catch { toast.warning("Salvataggio non riuscito"); }
  };

  const remove = async (id) => { try { await api.delete(`/tipi-pratica/${id}`); qc.invalidateQueries({ queryKey: ["tipi"] }); toast.success("Eliminato"); } catch { toast.warning("Errore"); } };

  return (
    <div data-testid="tipi-pratica-page">
      <div className="flex items-center justify-between mb-5">
        <div>
          <p className="text-[10px] font-medium uppercase tracking-[0.14em] text-[#8a93a6] mb-0.5">Configurazione</p>
          <h1 className="font-serif text-[22px] text-[#1A2B4A]">Tipi Pratica</h1>
        </div>
        <button data-testid="new-tipo-btn" onClick={openNew}
          className="inline-flex items-center gap-2 bg-[#1A2B4A] text-white px-4 py-2 rounded-sm hover:bg-[#22345c] transition-colors text-sm">
          <Plus className="w-4 h-4" /> Nuovo tipo
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {tipi.map((t) => (
          <div key={t.id} data-testid="tipo-card" className="bg-white border border-[#1A2B4A]/10 rounded-sm p-4">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="font-serif text-xl text-[#1A2B4A]">{t.nome}</h3>
                <p className="text-xs text-[#4a5568] mt-0.5">Importo standard: € {t.importo_default}</p>
              </div>
              <div className="flex items-center gap-2">
                <button data-testid={`duplica-tipo-${t.id}`} onClick={() => duplica(t)} title="Duplica questo tipo di pratica"
                  className="text-[#1A2B4A]/60 hover:text-[#C9A84C]"><CopyPlus className="w-4 h-4" /></button>
                <button data-testid={`edit-tipo-${t.id}`} onClick={() => openEdit(t)} className="text-[#1A2B4A]/60 hover:text-[#C9A84C]"><Pencil className="w-4 h-4" /></button>
                <button data-testid={`delete-tipo-${t.id}`} onClick={() => remove(t.id)} className="text-[#9b2c2c]/60 hover:text-[#9b2c2c]"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
            <ol className="mt-4 space-y-1.5">
              {(t.steps || []).map((s, i) => (
                <li key={`${t.id}-step-${i}`} className="text-sm flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-[#f9f8f5] border border-[#1A2B4A]/10 text-[10px] flex items-center justify-center text-[#4a5568]">{i + 1}</span>
                  {s.nome}
                </li>
              ))}
              {(t.steps || []).length === 0 && <li className="text-xs text-[#4a5568]">Nessuno step definito.</li>}
            </ol>
          </div>
        ))}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-[#16233c]/40 backdrop-blur-sm flex items-center justify-center p-4"
          onMouseDown={(e) => { if (e.target === e.currentTarget) closeDialog(); }}>
          <div className="bg-white rounded-lg max-w-lg w-full p-4 border-t-2 border-[#C9A84C] shadow-lg max-h-[90vh] overflow-y-auto">
            <h3 className="font-serif text-2xl text-[#1A2B4A] mb-5">{editingId ? "Modifica tipo pratica" : "Nuovo tipo pratica"}</h3>
            <label className="text-[10px] font-medium uppercase tracking-[0.12em] text-[#8a93a6]">Nome</label>
            <input data-testid="tipo-nome" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })}
              className="w-full mt-1 mb-4 px-3 py-2 bg-white border border-[#1A2B4A]/20 rounded-sm focus:outline-none focus:border-[#C9A84C]" />
            {form.codice && (
              <div className="mb-4 rounded-md border border-[#1A2B4A]/12 bg-[#f9f8f5] px-3 py-2">
                <div className="text-[10px] font-medium uppercase tracking-[0.12em] text-[#8a93a6]">Codice (identità tecnica, non modificabile)</div>
                <code data-testid="tipo-codice" className="text-[12px] text-[#1A2B4A]">{form.codice}</code>
                <p className="text-[11px] text-[#4a5568] mt-1">Puoi rinominare il tipo liberamente: gli automatismi (scadenze, migrazione, contenuti) seguono il codice, non il nome.</p>
              </div>
            )}
            <label className="text-[10px] font-medium uppercase tracking-[0.12em] text-[#8a93a6]">Descrizione</label>
            <p className="text-[11px] text-[#4a5568] mt-0.5 mb-1">Una riga che spiega a cosa serve: la vedi nella scelta del tipo in «Nuova pratica».</p>
            <input data-testid="tipo-descrizione" value={form.descrizione} onChange={(e) => setForm({ ...form, descrizione: e.target.value })}
              placeholder="A cosa serve questa pratica"
              className="w-full mt-1 mb-4 px-3 py-2 bg-white border border-[#1A2B4A]/20 rounded-sm focus:outline-none focus:border-[#C9A84C]" />
            <label className="text-[10px] font-medium uppercase tracking-[0.12em] text-[#8a93a6]">Esigenza del cliente (gruppo)</label>
            <p className="text-[11px] text-[#4a5568] mt-0.5 mb-1">Prima domanda in «Nuova pratica»: determina in quale voce compare questo tipo.</p>
            <select data-testid="tipo-gruppo" value={form.gruppo || ""} onChange={(e) => setForm({ ...form, gruppo: e.target.value })}
              className="w-full mt-1 mb-4 px-3 py-2 bg-white border border-[#1A2B4A]/20 rounded-sm focus:outline-none focus:border-[#C9A84C]">
              <option value="">— non assegnato (visibile solo in «vedi tutti i tipi») —</option>
              {(gruppi || []).map((g) => <option key={g.key} value={g.key}>{g.label}</option>)}
            </select>
            <label className="text-[10px] font-medium uppercase tracking-[0.12em] text-[#8a93a6]">Importo standard (€)</label>
            <input data-testid="tipo-importo" type="number" value={form.importo_default} onChange={(e) => setForm({ ...form, importo_default: e.target.value })}
              className="w-full mt-1 mb-4 px-3 py-2 bg-white border border-[#1A2B4A]/20 rounded-sm focus:outline-none focus:border-[#C9A84C]" />
            <label className="text-[10px] font-medium uppercase tracking-[0.12em] text-[#8a93a6]">Checklist documentale</label>
            <p className="text-[11px] text-[#4a5568] mt-0.5 mb-2">
              Documenti necessari per procedere con questo tipo di pratica. Compariranno in cima alla scheda pratica
              con spunta o alert e pulsante di caricamento nella cartella Drive del cliente.
            </p>
            <div className="flex gap-2 mb-2">
              <input data-testid="tipo-doc-nome" value={docNome} onChange={(e) => setDocNome(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addDoc())}
                placeholder="Documento (es. Passaporto, CUD, Modulo TFR…)"
                className="flex-1 px-3 py-2 text-sm bg-white border border-[#1A2B4A]/20 rounded-sm focus:outline-none focus:border-[#C9A84C]" />
              <input data-testid="tipo-doc-max" type="number" step="0.5" min="0" value={docMax} onChange={(e) => setDocMax(e.target.value)}
                placeholder="MB max" title="Peso massimo accettato in MB"
                className="w-24 px-2 py-2 text-sm bg-white border border-[#1A2B4A]/20 rounded-sm focus:outline-none focus:border-[#C9A84C]" />
              <button data-testid="tipo-add-doc" onClick={addDoc} className="px-3 bg-[#1A2B4A] text-white rounded-sm hover:bg-[#22345c]"><Plus className="w-4 h-4" /></button>
            </div>
            <button data-testid="tipo-doc-preset" onClick={addDocPreset}
              className="inline-flex items-center gap-1.5 text-[11px] border border-[#1A2B4A]/15 rounded-sm px-2 py-1 mb-2 hover:border-[#C9A84C]">
              <Plus className="w-3 h-3" /> Aggiungi documenti base (passaporto, codice fiscale, identità…)
            </button>
            <ul className="space-y-1.5 mb-6" data-testid="tipo-doc-list">
              {(form.documenti_richiesti || []).map((d, i) => (
                <li key={`doc-${i}`} className="flex items-center gap-2 bg-[#f9f8f5] px-2 py-1.5 rounded-sm">
                  <input data-testid={`tipo-doc-nome-${i}`} value={d.nome} onChange={(e) => patchDoc(i, { nome: e.target.value })}
                    className="flex-1 bg-transparent text-sm border border-transparent hover:border-[#1A2B4A]/15 focus:border-[#C9A84C] rounded-sm px-1 py-0.5 focus:outline-none" />
                  <label className="flex items-center gap-1.5 text-[11px] text-[#4a5568] shrink-0" title="Obbligatorio per procedere">
                    <input data-testid={`tipo-doc-obbl-${i}`} type="checkbox" checked={d.obbligatorio !== false}
                      onChange={() => patchDoc(i, { obbligatorio: !(d.obbligatorio !== false) })} className="accent-[#C9A84C]" />
                    obbl.
                  </label>
                  <input data-testid={`tipo-doc-max-${i}`} type="number" step="0.5" min="0" value={d.max_mb ?? ""}
                    onChange={(e) => patchDoc(i, { max_mb: e.target.value === "" ? null : Number(e.target.value) })}
                    placeholder="MB" title="Peso massimo in MB"
                    className="w-16 text-[11px] bg-white border border-[#1A2B4A]/15 rounded-sm px-1.5 py-1 focus:outline-none focus:border-[#C9A84C]" />
                  <button data-testid={`tipo-doc-remove-${i}`} onClick={() => removeDoc(i)}><X className="w-3.5 h-3.5 text-[#9b2c2c]" /></button>
                </li>
              ))}
              {!(form.documenti_richiesti || []).length && (
                <li className="text-[11px] text-[#8a93a6]">Nessun documento richiesto per questo tipo di pratica.</li>
              )}
            </ul>

            <label className="text-[10px] font-medium uppercase tracking-[0.12em] text-[#8a93a6]">Step del flusso</label>
            <div className="flex gap-2 mt-1 mb-3">
              <input data-testid="tipo-step-input" value={stepName} onChange={(e) => setStepName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addStep())}
                placeholder="Nome step…" className="flex-1 px-3 py-2 bg-white border border-[#1A2B4A]/20 rounded-sm focus:outline-none focus:border-[#C9A84C]" />
              <button data-testid="tipo-add-step" onClick={addStep} className="px-3 bg-[#1A2B4A] text-white rounded-sm"><Plus className="w-4 h-4" /></button>
            </div>
            <ul className="space-y-1.5 mb-6">
              {form.steps.map((s, i) => (
                <li key={s._k} className="flex items-center gap-2 bg-[#f9f8f5] px-2 py-1.5 rounded-sm group">
                  <span className="text-xs text-[#4a5568] w-4">{i + 1}.</span>
                  <input data-testid={`tipo-step-name-${i}`} value={s.nome} onChange={(e) => setStep(i, e.target.value)}
                    className="flex-1 bg-transparent text-sm border border-transparent hover:border-[#1A2B4A]/15 focus:border-[#C9A84C] rounded-sm px-1 py-0.5 focus:outline-none" />
                  <button onClick={() => moveStep(i, -1)} className="text-[#4a5568] hover:text-[#1A2B4A] opacity-0 group-hover:opacity-100"><ChevronUp className="w-4 h-4" /></button>
                  <button onClick={() => moveStep(i, 1)} className="text-[#4a5568] hover:text-[#1A2B4A] opacity-0 group-hover:opacity-100"><ChevronDown className="w-4 h-4" /></button>
                  <button data-testid={`tipo-step-remove-${i}`} onClick={() => removeStep(i)}><X className="w-3.5 h-3.5 text-[#9b2c2c]" /></button>
                </li>
              ))}
            </ul>
            <label className="text-[10px] font-medium uppercase tracking-[0.12em] text-[#8a93a6]">Dati del cliente indispensabili per questa pratica</label>
            <p className="text-[11px] text-[#4a5568] mt-0.5 mb-2">
              La scheda cliente ha sempre le stesse due sezioni (Dati anagrafici e Documenti). Qui spunti solo quali di quei dati
              devono essere già compilati per poter creare una pratica di questo tipo.
            </p>
            <div className="mb-3 max-h-64 overflow-y-auto border border-[#1A2B4A]/10 rounded-sm p-1" data-testid="tipo-req-list">
              {groupedReq.map((g) => (
                <div key={g.key} className="mb-1">
                  <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[#4a5568] px-3 pt-2 pb-1">
                    {g.label}
                    {g.key === "azienda" && <span className="normal-case tracking-normal font-normal text-[#8a6d1f]"> — richiesti solo ai clienti azienda</span>}
                  </div>
                  {g.fields.map((o) => (
                    <label key={o.key} className="flex items-center gap-2.5 text-sm text-[#1A2B4A] cursor-pointer px-3 py-1.5 rounded-sm hover:bg-[#f9f8f5]">
                      <input data-testid={`tipo-req-${o.key}`} type="checkbox" checked={form.campi_obbligatori.includes(o.key)} onChange={() => toggleReq(o.key)}
                        className="w-4 h-4 accent-[#C9A84C] shrink-0" />
                      <span className="flex-1">{o.label}</span>
                      {o.builtin === false && <span className="text-[9px] text-[#C9A84C] uppercase tracking-wide shrink-0">personalizzato</span>}
                    </label>
                  ))}
                </div>
              ))}
            </div>
            <label className="text-[10px] font-medium uppercase tracking-[0.12em] text-[#8a93a6]">Campi personalizzati di questo tipo di pratica</label>
            <p className="text-[11px] text-[#4a5568] mt-0.5 mb-2">
              Campi in più (es. "Numero fondo TFR", "Nome dello sponsor") che compaiono <b>solo nelle pratiche di questo tipo</b>
              e in nessun'altra. Si compilano dentro la singola pratica e non entrano nell'anagrafica del cliente.
              La <b>sezione</b> è il titolo del riquadro in cui raggruppare i campi nella scheda pratica (es. "Sponsor", "Fondo TFR"): se la lasci vuota vanno nel riquadro generale.
            </p>
            <div className="flex flex-wrap gap-2 mb-2">
              <input data-testid="tipo-campo-pratica-label" value={cpLabel} onChange={(e) => setCpLabel(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCampoPratica())}
                placeholder="Nome del campo (es. Nome e cognome sponsor)…"
                className="flex-1 min-w-[200px] px-3 py-2 text-sm bg-white border border-[#1A2B4A]/20 rounded-sm focus:outline-none focus:border-[#C9A84C]" />
              <input data-testid="tipo-campo-pratica-gruppo" value={cpGruppo} onChange={(e) => setCpGruppo(e.target.value)}
                placeholder="Sezione / riquadro (facoltativo)"
                className="w-40 px-3 py-2 text-sm bg-white border border-[#1A2B4A]/20 rounded-sm focus:outline-none focus:border-[#C9A84C]" />
              <button data-testid="tipo-add-campo-pratica" onClick={addCampoPratica} className="px-3 bg-[#1A2B4A] text-white rounded-sm hover:bg-[#22345c]"><Plus className="w-4 h-4" /></button>
            </div>
            <button data-testid="tipo-add-sponsor-preset" onClick={addSponsorPreset}
              className="text-[11px] underline text-[#8a6d1f] hover:text-[#1A2B4A] mb-2">Aggiungi il blocco Sponsor completo</button>
            <ul className="space-y-1.5 mb-6" data-testid="tipo-campi-pratica-list">
              {(form.campi_pratica || []).map((c, i) => (
                <li key={`${c.key || c.label}-${i}`} className="flex items-center gap-2 bg-[#f9f8f5] px-2 py-1.5 rounded-sm text-sm">
                  <span className="flex-1 truncate">
                    {c.label}
                    {c.gruppo && <span className="text-[11px] text-[#8a6d1f]"> · {c.gruppo}</span>}
                  </span>
                  <label className="flex items-center gap-1 text-[11px] text-[#4a5568] cursor-pointer">
                    <input data-testid={`tipo-campo-pratica-obbl-${i}`} type="checkbox" checked={!!c.obbligatorio}
                      onChange={() => toggleCampoObbl(i)} className="w-3.5 h-3.5 accent-[#C9A84C]" /> obbligatorio
                  </label>
                  <button data-testid={`tipo-campo-pratica-remove-${i}`} onClick={() => removeCampoPratica(i)}><X className="w-3.5 h-3.5 text-[#9b2c2c]" /></button>
                </li>
              ))}
              {(form.campi_pratica || []).length === 0 && <li className="text-[11px] text-[#4a5568] px-1">Nessun campo specifico per questo tipo di pratica.</li>}
            </ul>
            <div className="flex justify-end gap-3">
              <button data-testid="tipo-cancel" onClick={closeDialog} className="px-4 py-2 text-sm border border-[#1A2B4A] rounded-sm hover:bg-[#f9f8f5]">Annulla</button>
              <button data-testid="tipo-create" onClick={save} className="px-4 py-2 text-sm bg-[#1A2B4A] text-white rounded-sm hover:bg-[#22345c]">Salva</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
